import * as React from 'react'
import { lazy, Suspense, useEffect } from 'react'
import type { JSX } from 'react'
import {
  Navigate,
  Route,
  useLocation,
  useMatch,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { Loader, useLocalStorage } from '@buffer-mono/popcorn'

import {
  DEFAULT_VIEW_MODE,
  LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
} from '~publish/components/Calendar/hooks/useCalendar'
import type { CalendarDateRangeViewMode } from '~publish/components/Calendar/hooks/useCalendarDateRangeViewMode'
import {
  IdeaCreationDialog,
  IdeaDetailsDialog,
  IdeaEditDialog,
} from '~publish/components/IdeaManagementRouter'
import {
  ContentItemDetailsDialog,
  PostCreationDialog,
  PostDetailsDialog,
  PostEditDialog,
} from '~publish/components/PostDialogs'
import { AnalyzeRedirect } from '~publish/components/AnalyzeRedirect'
import { ContentItemPermalink } from '~publish/components/ContentItemPermalink'
import { PostPermalink } from '~publish/components/PostPermalink'
import { useUnlockedChannels } from '~publish/contexts/AccountContext'
import { env } from '~publish/env'
import { buildStartPageEditorUrl } from '~publish/helpers/externalUrls'
import { useBoardViewReady } from '~publish/hooks/useBoardViewEnabled'
import {
  channelSchedule,
  contentItemNotesRoute,
  draftPermalinkRoute,
  newCalendarSingleChannel,
  postPermalinkRoute,
  schedule,
} from '~publish/legacy/routes'
import { AllChannelsPage } from '~publish/pages/AllChannels'
import { BoardPage } from '~publish/pages/AllChannels/Board'
import CalendarPage from '~publish/pages/Calendar/CalendarPage'
import { ChannelPage } from '~publish/pages/Channel'
import { AnalyticsPage } from '~publish/pages/Channel/AnalyticsPage'
import { ChannelSettings } from '~publish/pages/Channel/ChannelSettings'
import { ChannelShopGrid } from '~publish/pages/Channel/ChannelShopGrid'
import { CommentDetailsDialog } from '~publish/pages/Community/CommentDetails'
import { MentionDetailsDialog } from '~publish/pages/Community/MentionDetails'
import { CommunityPage } from '~publish/pages/Community/CommunityPage'
import { SingleChannelView } from '~publish/pages/Community/SingleChannelView/SingleChannelView'
import CreatePage from '~publish/pages/Create'
import { AllFeedsList } from '~publish/pages/Create/Feed/components/AllFeedsList'
import { CollectionListV2 } from '~publish/pages/Create/Feed/components/CollectionListV2'
import { FeedListV2 } from '~publish/pages/Create/Feed/components/FeedListV2'
import { IdeasPage } from '~publish/pages/Create/IdeasPage'
import { TemplateDialogRoute } from '~publish/pages/Create/Templates/components/Template/TemplateDialogRoute'
import { TemplateLibraryPage } from '~publish/pages/Create/Templates/components/TemplateLibraryPage/TemplateLibraryPage'
import { HomeRoute } from '~publish/pages/Home/HomeRoute'
import {
  HabitsRoute,
  InsightsChannel,
  InsightsPageOrSingleChannelRedirect,
} from '~publish/pages/Insights'
import { NewPostPage } from '~publish/pages/NewPost'
import AffiliateProgram from '~publish/pages/Settings/AffiliateProgram/AffiliateProgram'
import AppsExtrasPage from '~publish/pages/Settings/AppsExtras/AppsExtrasPage'
import BetaFeatures from '~publish/pages/Settings/BetaFeatures/BetaFeatures'
import { Billing } from '~publish/pages/Settings/Billing/Billing'
import { ChannelGroups as SettingsChannelGroups } from '~publish/pages/Settings/ChannelGroups/ChannelGroups'
import Channels from '~publish/pages/Settings/Channels/Channels'
import { DeveloperDashboard } from '~publish/pages/Settings/DeveloperDashboard'
import { APIClientPage } from '~publish/pages/Settings/DeveloperDashboard/components/APIClientPage'
import EmailNotifications from '~publish/pages/Settings/EmailNotifications/EmailNotifications'
import GeneralSettings from '~publish/pages/Settings/General/General'
import Import from '~publish/pages/Settings/Import/Import'
import { Integrations } from '~publish/pages/Settings/Integrations'
import Preferences from '~publish/pages/Settings/Preferences/Preferences'
import ProfileSettings from '~publish/pages/Settings/Profile/ProfileSettings'
import ReferAFriend from '~publish/pages/Settings/ReferAFriend/ReferAFriend'
import RoleAndPermissions from '~publish/pages/Settings/RoleAndPermissions/RoleAndPermissions'
import { SavedRepliesSettings } from '~publish/pages/Settings/SavedReplies/SavedRepliesSettings'
import Settings from '~publish/pages/Settings/Settings'
import { Tags } from '~publish/pages/Settings/Tags/Tags'
import Team from '~publish/pages/Settings/Team/Team'

const TemplateManagerPage = React.lazy(
  () =>
    import('~publish/pages/Create/Templates/components/TemplateManagerPage/TemplateManagerPage'),
)

const ChannelAuthorizationPage = lazy(() =>
  import('~publish/pages/ChannelAuthorization').then((m) => ({
    default: m.ChannelAuthorizationPage,
  })),
)

const ChannelAuthorizationDevHarness = lazy(
  () =>
    import('~publish/pages/ChannelAuthorization/__dev/ChannelAuthorizationDevHarness'),
)

import ComposePage from '../../../compose-page'
import { CommentsRouteHandler } from '../CommentsRouteHandler/CommentsRouteHandler'
import { InsightsRouteHandler } from '../InsightsRouteHandler/InsightsRouteHandler'
import { PostDeepLinkResolver } from '../PostDeepLinkResolver/PostDeepLinkResolver'
import { DefaultPath } from './DefaultPath'

const LegacyTagsRedirect = (): JSX.Element => {
  return <Navigate to="/settings/tags" replace />
}

const LegacyTagsNewRedirect = (): JSX.Element => {
  return <Navigate to="/settings/tags/new" replace />
}

const LegacyTagsEditRedirect = (): JSX.Element => {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/settings/tags/${id}/edit`} replace />
}

const TagViewRedirect = (): JSX.Element => {
  const params = useParams()
  return (
    <Navigate
      replace
      to={`/all-channels${params.id ? `?tags[]=${params.id}` : ''}${
        params.tab ? `&tab=${params.tab}` : ''
      }`}
    />
  )
}

/**
 * The invitation email link lands here (`/invite/accept?invite=<jwt>`). Rather
 * than render a dedicated accept page, we drop the user wherever they would
 * normally land and show the invitation modal on top (see
 * {@link InvitationModalContainer}). The JWT (and the `new` brand-new-user
 * marker the login app adds) ride along in router `state` so they stay out of
 * the visible URL once the user is on their landing page.
 */
const InviteAcceptRedirect = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const inviteJwt = searchParams.get('invite')
  const inviteIsNewUser = searchParams.get('new') === '1'

  if (!inviteJwt) {
    return <Navigate to="/" replace />
  }

  return <Navigate to="/" replace state={{ inviteJwt, inviteIsNewUser }} />
}

const StartPageEditorRedirect = (): JSX.Element => {
  const params = useParams<{ id: string }>()
  const { id } = params
  useEffect(() => {
    if (id) {
      window.location.href = buildStartPageEditorUrl(id)
    }
  }, [id])
  return <></>
}

export const ChannelIdRedirect = (): JSX.Element => {
  const params = useParams<{ id: string }>()
  const location = useLocation()
  const { id } = params
  return <Navigate to={`/channels/${id}/schedule${location.search}`} replace />
}

const ChannelPostsToScheduleRedirect = (): JSX.Element => {
  const params = useParams<{ id: string; '*': string }>()
  const location = useLocation()
  const { id, '*': wildcardPath } = params
  const newPath = `/channels/${id}/schedule${
    wildcardPath ? `/${wildcardPath}` : ''
  }${location.search}`
  return <Navigate to={newPath} replace />
}

const AllChannelsToScheduleRedirect = (): JSX.Element => {
  const params = useParams<{ '*': string }>()
  const location = useLocation()
  const wildcardPath = params['*'] || ''
  const newPath = `/schedule${wildcardPath ? `/${wildcardPath}` : ''}${
    location.search
  }`
  return <Navigate to={newPath} replace />
}

const CommentsToCommunityRedirect = (): JSX.Element => {
  const params = useParams<{ '*': string }>()
  const location = useLocation()
  const wildcardPath = params['*'] || ''

  // Handle both /comments and /comments/:channelId/* patterns
  // Convert /comments/:channelId to /channels/:channelId/community
  if (wildcardPath) {
    const pathParts = wildcardPath.split('/')
    const channelId = pathParts[0]
    const remainingPath = pathParts.slice(1).join('/')

    // Default to posts view when no explicit path
    const basePath = remainingPath
      ? `/channels/${channelId}/community/${remainingPath}`
      : `/channels/${channelId}/community/posts`
    const newPath = `${basePath}${location.search}`
    return <Navigate to={newPath} replace />
  }

  // /comments without channel ID goes to /community/posts (default by-post view)
  const newPath = `/community/posts${location.search}`
  return <Navigate to={newPath} replace />
}

const FeedCollectionRedirect = (): JSX.Element => {
  const params = useParams<{ feedCollectionId: string; '*': string }>()
  const { feedCollectionId, '*': wildcardPath } = params
  const newPath = `/create/feeds/collections/${feedCollectionId}${
    wildcardPath ? `/${wildcardPath}` : ''
  }`
  return <Navigate to={newPath} replace />
}

const IdeaPermalinkRedirect = (): JSX.Element => {
  const params = useParams<{ ideaId: string }>()
  const { ideaId } = params
  return <Navigate to={`/create/ideas/${ideaId}`} replace />
}

const CalendarMonthRedirect = (): JSX.Element => {
  const params = useParams<{ '*': string }>()
  const location = useLocation()
  const wildcardPath = params['*'] || ''
  const newPath = `/schedule/calendar/month${
    wildcardPath ? `/${wildcardPath}` : ''
  }${location.search}`
  return <Navigate to={newPath} replace />
}

const CalendarWeekRedirect = (): JSX.Element => {
  const params = useParams<{ '*': string }>()
  const location = useLocation()
  const wildcardPath = params['*'] || ''
  const newPath = `/schedule/calendar/week${
    wildcardPath ? `/${wildcardPath}` : ''
  }${location.search}${location.hash}`
  return <Navigate to={newPath} replace />
}

const CalendarRedirect = (): JSX.Element => {
  const location = useLocation()
  const [calendarDateRangeViewMode] =
    useLocalStorage<CalendarDateRangeViewMode>(
      LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
      DEFAULT_VIEW_MODE,
    )
  const newPath = `/schedule/calendar/${calendarDateRangeViewMode}${location.search}`
  return <Navigate to={newPath} replace />
}

/**
 * Wrapper component that redirects to single channel schedule when only one channel is connected.
 * Otherwise renders the AllChannelsPage.
 *
 * Only the bare `/schedule` URL redirects. Anything nested under it is a dialog
 * opened by a pasted link; the channel view has no path to redirect that to, so
 * redirecting would drop it.
 */
export const SchedulePageOrSingleChannelRedirect = (): JSX.Element => {
  const location = useLocation()
  const isBareScheduleRoute = useMatch(schedule.route) !== null
  const unlockedChannels = useUnlockedChannels()
  const hasSingleChannel = unlockedChannels?.length === 1

  if (hasSingleChannel && isBareScheduleRoute) {
    const channelId = unlockedChannels[0]?.id
    return (
      <Navigate
        to={{
          pathname: channelSchedule.buildPathname({ id: channelId }),
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    )
  }

  return <AllChannelsPage />
}

/**
 * Wrapper component that redirects to single channel calendar when only one channel is connected.
 * Otherwise renders the CalendarPage.
 */
export const ScheduleCalendarOrSingleChannelRedirect = (): JSX.Element => {
  const params = useParams<{ granularity: 'week' | 'month' }>()
  const location = useLocation()
  const unlockedChannels = useUnlockedChannels()
  const hasSingleChannel = unlockedChannels?.length === 1
  const [calendarDateRangeViewMode] =
    useLocalStorage<CalendarDateRangeViewMode>(
      LAST_VIEW_MODE_LOCAL_STORAGE_KEY,
      DEFAULT_VIEW_MODE,
    )

  if (hasSingleChannel) {
    const channelId = unlockedChannels[0]?.id
    const granularity = params.granularity ?? calendarDateRangeViewMode
    return (
      <Navigate
        to={{
          pathname: newCalendarSingleChannel.buildPathname({
            id: channelId,
            granularity,
          }),
          search: location.search,
          hash: location.hash,
        }}
        replace
      />
    )
  }

  return <CalendarPage />
}

/**
 * Gates `/schedule/board` and `/channels/:id/schedule/board` behind
 * `geid-publish-board-view` — a direct/bookmarked hit with the split off
 * falls back to the equivalent List route instead of 404ing.
 */
const BoardViewGuard = (): JSX.Element => {
  const params = useParams<{ id?: string }>()
  const location = useLocation()
  const { isEnabled: isBoardViewEnabled, isReady } = useBoardViewReady()

  // Wait for the split to resolve before deciding — redirecting on the
  // pre-ready default (false) would bounce a flag-on user back to List on
  // every deep link or refresh.
  if (!isReady) {
    return <Loader />
  }

  if (!isBoardViewEnabled) {
    const fallbackRoute = params.id
      ? channelSchedule.getRoute(params.id)
      : schedule.getRoute()
    return (
      <Navigate
        to={{
          pathname: fallbackRoute.split('?')[0],
          search: location.search,
        }}
        replace
      />
    )
  }

  return <BoardPage />
}

interface AppRoutesProps {
  shouldPreOpenChannelStorefront: boolean
  showBetaFeatures: boolean
  loadTeamRoute: boolean
}

/**
 * Renders the application's route table.
 *
 * This is a render function (not a React component) that returns Route elements
 * to be used directly within a Routes component.
 *
 * Note: Redirect components are defined within this file for cohesion.
 */
export function renderAppRoutes({
  shouldPreOpenChannelStorefront,
  showBetaFeatures,
  loadTeamRoute,
}: AppRoutesProps): JSX.Element {
  return (
    <>
      <Route path={'/compose'} element={<ComposePage />} />

      {/* Team-invitation acceptance: redirect to the normal landing page and
          let InvitationModalContainer surface the accept/welcome modal on top
          (CORE-6022). The invite JWT rides along in router state. */}
      <Route path="/invite/accept" element={<InviteAcceptRedirect />} />

      <Route path={'/link-to-post'} element={<PostDeepLinkResolver />} />

      <Route path={postPermalinkRoute.route} element={<PostPermalink />} />
      <Route
        path={draftPermalinkRoute.route}
        element={<ContentItemPermalink />}
      />
      <Route
        path={contentItemNotesRoute.route}
        element={<ContentItemPermalink notes />}
      />

      <Route path="/post/new" element={<NewPostPage />} />

      {/* Dev/staging harness for testing channel authorization UI components */}
      {env.VITE_NODE_ENV !== 'production' && (
        <Route
          path="/channels/authorization/__dev"
          element={
            <Suspense fallback={<Loader />}>
              <ChannelAuthorizationDevHarness />
            </Suspense>
          }
        />
      )}

      {/* Post-OAuth channel authorization (Phase 2 of account deprecation) */}
      <Route
        path="/channels/authorization/:service"
        element={
          <Suspense fallback={<Loader />}>
            <ChannelAuthorizationPage />
          </Suspense>
        }
      />

      {/* Channel-specific routes */}
      <Route path="/channels/:id/settings" element={<ChannelSettings />} />
      <Route path="/channels/:id/shop-grid" element={<ChannelShopGrid />} />
      <Route
        path="/channels/:id/calendar/:granularity"
        element={<CalendarPage />}
      >
        <Route path="posts/new" element={<PostCreationDialog />} />
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="posts/:postId/edit" element={<PostEditDialog />} />
        <Route
          path="drafts/:contentItemId"
          element={<ContentItemDetailsDialog />}
        />
        <Route path="*" element={null} />
      </Route>

      {/* Tags (formerly Campaigns) */}
      <Route path="/campaigns/new" element={<LegacyTagsNewRedirect />} />
      <Route path="/campaigns/:id/edit" element={<LegacyTagsEditRedirect />} />
      <Route path="/campaigns/:id/:tab" element={<TagViewRedirect />} />
      <Route path="/campaigns" element={<LegacyTagsRedirect />} />
      <Route path="/tags/new" element={<LegacyTagsNewRedirect />} />
      <Route path="/tags/:id/edit" element={<LegacyTagsEditRedirect />} />
      <Route path="/tags/:id/*" element={<LegacyTagsEditRedirect />} />
      <Route path={'/tags'} element={<LegacyTagsRedirect />} />
      <Route
        path="/channel-groups"
        element={<Navigate to="/settings/channel-groups" replace />}
      />

      {/* Insights (always available — sidebar visibility is controlled by Split flag) */}
      <Route path="/insights" element={<InsightsRouteHandler />}>
        {/*
         * Pathless layout route: keeps the all-channels page mounted while a
         * post dialog renders in its <Outlet/>. An `index` route can't host
         * children, and a plain sibling route would unmount the page.
         */}
        <Route element={<InsightsPageOrSingleChannelRedirect />}>
          <Route index element={null} />
          <Route path="posts/:postId" element={<PostDetailsDialog />} />
          <Route path="posts/:postId/edit" element={<PostEditDialog />} />
        </Route>
        <Route path="habits" element={<HabitsRoute />} />
        <Route path=":channelId" element={<InsightsChannel />}>
          <Route path="posts/:postId" element={<PostDetailsDialog />} />
          <Route path="posts/:postId/edit" element={<PostEditDialog />} />
        </Route>
        <Route path="*" element={null} />
      </Route>

      {/* Settings */}
      <Route path="/settings" element={<Settings />}>
        <Route index element={<ProfileSettings />} />
        <Route path="preferences" element={<Preferences />} />
        <Route path="notifications" element={<EmailNotifications />} />
        <Route path="apps-extras" element={<AppsExtrasPage />} />
        <Route path="general" element={<GeneralSettings />} />
        <Route
          path="channels"
          element={
            <Channels
              preOpenChannelStorefront={shouldPreOpenChannelStorefront}
            />
          }
        />
        <Route path="billing" element={<Billing />} />
        <Route path="billing/keep-my-plan" element={<Billing />} />
        <Route
          path="channels/:id"
          element={<ChannelSettings isForNewSettings={true} />}
        />
        <Route path="refer-a-friend" element={<ReferAFriend />} />
        <Route path="partner-with-buffer" element={<AffiliateProgram />} />
        <Route
          path="beta"
          element={
            showBetaFeatures ? (
              <BetaFeatures />
            ) : (
              <Navigate to="/settings" replace />
            )
          }
        />
        <Route
          path="team"
          element={
            loadTeamRoute ? <Team /> : <Navigate to="/settings" replace />
          }
        />
        <Route path="role-and-permissions" element={<RoleAndPermissions />} />
        <Route path="saved-replies" element={<SavedRepliesSettings />} />
        <Route path="tags" element={<Tags />}>
          <Route path="new" element={null} />
          <Route path=":tagId/edit" element={null} />
          <Route path="*" element={null} />
        </Route>
        <Route path="channel-groups" element={<SettingsChannelGroups />} />
        <Route path="api" element={<DeveloperDashboard />} />
        <Route path="api/client/:clientId" element={<APIClientPage />} />
        <Route
          path="api/client"
          element={<Navigate to="/settings/api?tab=app-clients" replace />}
        />
        <Route path="import" element={<Import />} />
        <Route path="integrations" element={<Integrations />}>
          <Route path=":integrationId" element={null} />
          <Route path="*" element={null} />
        </Route>
        <Route path="*" element={<Navigate to="/settings" replace />} />
      </Route>

      {/* Legacy /content redirects (always available) */}
      <Route
        path="/content/ideas/new" // Because of some old routing, it's possible to end up here
        element={
          <Navigate
            to={{
              ...location,
              pathname: '/create/ideas/new',
            }}
          />
        }
      />
      <Route
        path="/content/new"
        element={
          <Navigate
            to={{
              ...location,
              pathname: '/create/ideas/new',
            }}
          />
        }
      />
      <Route
        path="/content"
        element={
          <Navigate
            to={{
              ...location,
              pathname: '/create',
            }}
          />
        }
      />

      {/* Schedule / Previously All Channels */}
      {/* When only one channel is connected, redirect to that channel's schedule/calendar */}
      <Route
        path="/schedule/calendar/:granularity"
        element={<ScheduleCalendarOrSingleChannelRedirect />}
      >
        <Route path="posts/new" element={<PostCreationDialog />} />
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="posts/:postId/edit" element={<PostEditDialog />} />
        <Route
          path="drafts/:contentItemId"
          element={<ContentItemDetailsDialog />}
        />
        <Route path="*" element={null} />
      </Route>
      {/* Board view — behind the geid-publish-board-view split, @see useBoardViewEnabled */}
      <Route path="/schedule/board" element={<BoardViewGuard />}>
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="*" element={null} />
      </Route>
      <Route path="/schedule" element={<SchedulePageOrSingleChannelRedirect />}>
        <Route path="posts/new" element={<PostCreationDialog />} />
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="posts/:postId/edit" element={<PostEditDialog />} />
        <Route
          path="drafts/:contentItemId"
          element={<ContentItemDetailsDialog />}
        />
        <Route path="*" element={null} />
      </Route>

      {/* Channels View: uses new /schedule path */}
      <Route path="/channels/:id/schedule/board" element={<BoardViewGuard />}>
        <Route path="posts/:postId" element={<PostDetailsDialog />} />
        <Route path="*" element={null} />
      </Route>
      <Route path="/channels/:id/schedule" element={<ChannelPage />}>
        <Route path="new" element={<PostCreationDialog />} />
        <Route path=":postId" element={<PostDetailsDialog />} />
        <Route path=":postId/edit" element={<PostEditDialog />} />
        <Route path="*" element={null} />
      </Route>

      {/* External redirects */}
      <Route path="/analyze" element={<AnalyzeRedirect />} />
      <Route path="/channels/:id/analytics" element={<AnalyticsPage />} />
      <Route
        path="/channels/:id/editor"
        element={<StartPageEditorRedirect />}
      />
      {/* Backward compatibility: redirect /channels/:id/posts/* to /channels/:id/schedule/* */}
      <Route
        path="/channels/:id/posts/*"
        element={<ChannelPostsToScheduleRedirect />}
      />
      <Route path="/channels/:id" element={<ChannelIdRedirect />} />

      {/* All channels Community View: uses new /community path, previously /comments */}
      <Route path="/community" element={<CommentsRouteHandler />}>
        {/* Index = list view; /posts = by-post view. Sidebar/nav links use /posts as default. */}
        <Route element={<CommunityPage />}>
          <Route index element={null} />
          <Route path="post/new" element={<PostCreationDialog />} />
          <Route path="post/:postId" element={<PostDetailsDialog />} />
          <Route path="post/:postId/edit" element={<PostEditDialog />} />
          {/* One route for both the plain link and its by-post form, so the
              dialog stays mounted when it moves under the comment's post. */}
          <Route
            path="by-post?/:postId?/comment/:commentId"
            element={<CommentDetailsDialog />}
          />
          <Route path="mention/:mentionId" element={<MentionDetailsDialog />} />
        </Route>
        {/* By post view - shows posts list with comments */}
        <Route path="posts" element={<CommunityPage />}>
          <Route index element={null} />
          <Route path="new" element={<PostCreationDialog />} />
          <Route path=":postId" element={<PostDetailsDialog />} />
          <Route path=":postId/edit" element={<PostEditDialog />} />
        </Route>
        {/* Direct post selection in by-post view */}
        <Route path="by-post/:postId" element={<CommunityPage />}>
          <Route path="posts/new" element={<PostCreationDialog />} />
          <Route path="posts/:postId" element={<PostDetailsDialog />} />
          <Route path="posts/:postId/edit" element={<PostEditDialog />} />
        </Route>
        <Route path="*" element={null} />
      </Route>

      {/* Individual Channel Community View: uses new /channels/:channelId/community path, previously /comments/:channelId/* */}
      <Route
        path="/channels/:channelId/community"
        element={<CommentsRouteHandler />}
      >
        {/* Pathless layout route to allow dialogs to be nested under SingleChannelView */}
        <Route element={<SingleChannelView />}>
          <Route index element={null} />
          <Route path="post/:postId" element={<PostDetailsDialog />} />
          <Route path="post/:postId/edit" element={<PostEditDialog />} />
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
        </Route>
        <Route path=":view" element={<SingleChannelView />}>
          <Route path="posts/new" element={<PostCreationDialog />} />
          <Route path="posts/:postId" element={<PostDetailsDialog />} />
          <Route path="posts/:postId/edit" element={<PostEditDialog />} />
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
        </Route>
        <Route path="by-post/:postId" element={<SingleChannelView />}>
          <Route path="posts/new" element={<PostCreationDialog />} />
          <Route path="posts/:postId" element={<PostDetailsDialog />} />
          <Route path="posts/:postId/edit" element={<PostEditDialog />} />
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
        </Route>
        <Route path="*" element={null} />
      </Route>

      {/* Create with new structure */}
      <Route element={<CreatePage />}>
        <Route
          path="/create"
          element={<Navigate to="/create/ideas" replace />}
        />
        <Route path="/create/ideas" element={<IdeasPage pageTitle="Ideas" />}>
          <Route path="new" element={<IdeaCreationDialog />} />
          <Route path=":ideaId/edit" element={<IdeaEditDialog />} />
          <Route path=":ideaId" element={<IdeaDetailsDialog />} />
        </Route>
        <Route path="/create/feeds/:feedId" element={<FeedListV2 />}>
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
        </Route>
        <Route
          path="/create/feeds/collections/:feedCollectionId"
          element={<CollectionListV2 />}
        >
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
        </Route>
        <Route path="/create/feeds" element={<AllFeedsList />}>
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="ideas/:ideaId/edit" element={<IdeaEditDialog />} />
          <Route path="ideas/:ideaId" element={<IdeaDetailsDialog />} />
          <Route path="*" element={null} />
        </Route>
        <Route path="/create/templates" element={<TemplateLibraryPage />}>
          <Route
            path="template/:templateId"
            element={<TemplateDialogRoute />}
          />
          <Route path="ideas/new" element={<IdeaCreationDialog />} />
          <Route path="*" element={null} />
        </Route>
      </Route>

      {/* Template Manager — standalone route outside CreatePage layout */}
      <Route
        path="/create/templates/manage"
        element={
          <React.Suspense fallback={null}>
            <TemplateManagerPage />
          </React.Suspense>
        }
      />

      {/* Backward compatibility redirects */}
      <Route path="/channels" element={<Navigate to="/schedule" replace />} />
      <Route
        path="/all-channels/*"
        element={<AllChannelsToScheduleRedirect />}
      />
      <Route path="/comments/*" element={<CommentsToCommunityRedirect />} />

      <Route path="/calendar" element={<CalendarRedirect />} />
      <Route path="/calendar/month/*" element={<CalendarMonthRedirect />} />
      <Route path="/calendar/week/*" element={<CalendarWeekRedirect />} />
      <Route
        path="/create/feed/collections/:feedCollectionId/*"
        element={<FeedCollectionRedirect />}
      />

      {/* Permalinks */}
      <Route path="/ideas/:ideaId" element={<IdeaPermalinkRedirect />} />

      <Route path="/home" element={<HomeRoute />} />

      {/* Home redirect */}
      <Route path="/" element={<DefaultPath />} />
    </>
  )
}
